import React, { useId, useMemo } from 'react';
import PropTypes from 'prop-types';

import Button from '../../atoms/Button';
import CloseButton from '../../atoms/CloseButton';
import GlassDatePicker from '../../molecules/GlassDatePicker';
import PanelDropdownField from '../../molecules/PanelDropdownField';
import PanelInputField from '../../molecules/PanelInputField';
import './DiscoverMarkerComposerCard.scss';

const DISCOVER_COMPOSER_ADDRESS_LISTBOX_ID = 'discover-composer-address-listbox';

const normalizeMarkerType = (type) => {
    if (!type) {
        return '';
    }
    return String(type).trim().toUpperCase();
};

const SCHEDULE_FIELDS = [
    { name: 'startDate', labelKey: 'discoverPage.composer.startsLabel', labelFallback: 'Starts' },
    { name: 'endDate', labelKey: 'discoverPage.composer.endsLabel', labelFallback: 'Ends' },
];

const isScheduledMarkerType = (type) => (
    type === 'EVENT' || type === 'SESSION'
);

const formatCoordinateLabel = (value) => {
    const coordinate = typeof value === 'number' ? value : Number.parseFloat(value);
    return Number.isFinite(coordinate) ? coordinate.toFixed(5) : '—';
};

export default function DiscoverMarkerComposerCard({
    t,
    children,
    isEditing = false,
    isTypeLocked = false,
    isSubmitting = false,
    isLocationResolving = false,
    isSaveDisabled = false,
    values,
    markerTypes,
    errorMessage = '',
    addressValue = '',
    addressFieldRef = null,
    addressSuggestions = [],
    addressHighlightedIndex = -1,
    isAddressOpen = false,
    isAddressLoading = false,
    showAddressNoResults = false,
    addressLookupError = null,
    locationError = '',
    onClose,
    onSubmit,
    onFieldChange,
    onAddressChange,
    onAddressKeyDown,
    onAddressFieldBlur,
    onOpenAddressDropdown,
    onAddressSuggestionSelect,
    onAddressSuggestionHighlight,
}) {
    const fieldIdPrefix = useId();
    const cancelLabel = t('discoverPage.composer.cancelLabel', 'Cancel');
    const saveLabel = isEditing
        ? t('discoverPage.composer.saveChangesLabel', 'Save changes')
        : t('discoverPage.composer.saveLabel', 'Save');
    const savingLabel = t('discoverPage.composer.savingLabel', 'Saving…');
    const namePlaceholder = t('discoverPage.composer.placeholders.name', 'Downtown Jam Session');
    const addressPlaceholder = t('discoverPage.composer.placeholders.address', '123 Main St');
    const typeOptions = useMemo(
        () => markerTypes.map((type) => ({
            value: type,
            label: type,
        })),
        [markerTypes],
    );
    const visibilityOptions = useMemo(
        () => [
            { value: 'PUBLIC', label: t('discoverPage.composer.visibility.public', 'Public') },
            { value: 'FRIENDS', label: t('discoverPage.composer.visibility.friends', 'Friends') },
            { value: 'ONLY_ME', label: t('discoverPage.composer.visibility.onlyMe', 'Only me') },
            { value: 'PRIVATE', label: t('discoverPage.composer.visibility.private', 'Private') },
        ],
        [t],
    );
    const composerRequiresSchedule = isScheduledMarkerType(normalizeMarkerType(values.type));
    const composerAddressActiveDescendant = addressHighlightedIndex >= 0
        && addressSuggestions[addressHighlightedIndex]
        ? `discover-composer-address-option-${addressHighlightedIndex}`
        : undefined;
    const typeField = (
        <PanelDropdownField
            className="discover-composer-card__input-field"
            label={t('discoverPage.composer.typeLabel', 'Type')}
            id={`${fieldIdPrefix}-type`}
            value={values.type}
            options={typeOptions}
            disabled={isSubmitting || isTypeLocked}
            noItemsMessage=""
            onChange={onFieldChange('type')}
        />
    );
    return (
        <section className="discover-composer-card" aria-label={t('discoverPage.composer.aria', 'Add marker form')}>
            <div className="discover-composer-card__header">
                <h3 className="discover-composer-card__title">
                    {isEditing
                        ? t('discoverPage.composer.editTitle', 'Edit marker')
                        : t('discoverPage.composer.createTitle', 'Add marker')}
                </h3>
                <CloseButton
                    className="close-button--on-card discover-composer-card__close"
                    ariaLabel={t('discoverPage.composer.closeLabel', 'Close marker form')}
                    onClick={onClose}
                    disabled={isSubmitting}
                />
            </div>
            {children ? (
                <div className="discover-composer-card__form">
                    {typeField}
                    <p className="discover-composer-card__tip discover-composer-card__tip--lead">
                        {t('discoverPage.composer.mapHint', 'Tip: click on the map to copy coordinates and resolve the address.')}
                    </p>
                    {isLocationResolving && (
                        <p role="status" className="discover-composer-card__hint">
                            {t('discoverPage.composer.resolvingAddressLabel', 'Resolving address…')}
                        </p>
                    )}
                    {locationError && <p role="status" className="discover-composer-card__error">{locationError}</p>}
                    {children}
                </div>
            ) : (
                <form className="discover-composer-card__form" onSubmit={onSubmit}>
                    <div className="discover-composer-card__grid">
                        <PanelInputField
                            className="discover-composer-card__input-field"
                            label={t('discoverPage.composer.nameLabel', 'Name')}
                            id={`${fieldIdPrefix}-name`}
                            type="text"
                            name="name"
                            value={values.name}
                            onChange={onFieldChange('name')}
                            disabled={isSubmitting}
                            placeholder={namePlaceholder}
                        />
                        {typeField}
                        <PanelDropdownField
                            className="discover-composer-card__input-field"
                            label={t('discoverPage.composer.visibilityLabel', 'Visibility')}
                            id={`${fieldIdPrefix}-privacy`}
                            value={values.privacy}
                            options={visibilityOptions}
                            disabled={isSubmitting}
                            noItemsMessage=""
                            onChange={onFieldChange('privacy')}
                        />
                        <PanelInputField
                            className="discover-composer-card__input-field discover-composer-card__input-field--full discover-composer-card__input-field--address"
                            fieldRef={addressFieldRef}
                            fieldProps={{ onBlur: onAddressFieldBlur }}
                            label={t('discoverPage.composer.addressLabel', 'Address')}
                            id={`${fieldIdPrefix}-address`}
                            type="text"
                            name="address"
                            value={addressValue}
                            onChange={onAddressChange}
                            onKeyDown={onAddressKeyDown}
                            onFocus={() => {
                                if (addressSuggestions.length > 0) {
                                    onOpenAddressDropdown();
                                }
                            }}
                            disabled={isSubmitting || isLocationResolving}
                            placeholder={addressPlaceholder}
                            autoComplete="off"
                            role="combobox"
                            aria-autocomplete="list"
                            aria-expanded={isAddressOpen}
                            aria-controls={DISCOVER_COMPOSER_ADDRESS_LISTBOX_ID}
                            aria-activedescendant={composerAddressActiveDescendant}
                        >
                            <dl
                                className="discover-composer-card__coordinates"
                                aria-label={t('discoverPage.composer.coordinatesLabel', 'Coordinates')}
                            >
                                <div className="discover-composer-card__coordinate">
                                    <dt>{t('discoverPage.composer.latitudeLabel', 'Latitude')}</dt>
                                    <dd>{formatCoordinateLabel(values.latitude)}</dd>
                                </div>
                                <div className="discover-composer-card__coordinate">
                                    <dt>{t('discoverPage.composer.longitudeLabel', 'Longitude')}</dt>
                                    <dd>{formatCoordinateLabel(values.longitude)}</dd>
                                </div>
                            </dl>
                            {isLocationResolving && (
                                <span className="discover-composer-card__hint">
                                    {t('discoverPage.composer.resolvingAddressLabel', 'Resolving address…')}
                                </span>
                            )}
                            {!isLocationResolving && isAddressLoading && (
                                <span className="discover-composer-card__hint">
                                    {t('discoverPage.composer.searchingAddressesLabel', 'Searching addresses…')}
                                </span>
                            )}
                            {showAddressNoResults && (
                                <div className="discover-composer-card__autocomplete-empty" role="status">
                                    {t('discoverPage.composer.noAddressResults', 'No results')}
                                </div>
                            )}
                            {addressLookupError && !isLocationResolving && (
                                <div className="discover-composer-card__autocomplete-error" role="status">
                                    {addressLookupError.message || t('discoverPage.composer.addressLookupFailed', 'Unable to load address suggestions.')}
                                </div>
                            )}
                            {locationError && (
                                <div className="discover-composer-card__autocomplete-error" role="status">
                                    {locationError}
                                </div>
                            )}
                            {isAddressOpen && addressSuggestions.length > 0 && (
                                <ul
                                    id={DISCOVER_COMPOSER_ADDRESS_LISTBOX_ID}
                                    className="discover-composer-card__autocomplete-list"
                                    role="listbox"
                                >
                                    {addressSuggestions.map((suggestion, index) => {
                                        const isHighlighted = index === addressHighlightedIndex;
                                        return (
                                            <li key={suggestion.id || `${suggestion.label}-${index}`} role="presentation">
                                                <button
                                                    id={`discover-composer-address-option-${index}`}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={isHighlighted}
                                                    className={`discover-composer-card__autocomplete-option${isHighlighted ? ' is-highlighted' : ''}`}
                                                    onMouseDown={(event) => {
                                                        event.preventDefault();
                                                        onAddressSuggestionSelect(suggestion);
                                                    }}
                                                    onFocus={() => onAddressSuggestionHighlight(index)}
                                                >
                                                    {suggestion.label}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </PanelInputField>
                        {composerRequiresSchedule && (
                            <>
                                {SCHEDULE_FIELDS.map(({ name, labelKey, labelFallback }) => {
                                    const fieldLabel = t(labelKey, labelFallback);
                                    return (
                                        <div
                                            key={name}
                                            className="panel-input-field discover-composer-card__input-field"
                                        >
                                            {/* <label for> cannot name a <button>. */}
                                            <span className="panel-input-field__label">{fieldLabel}</span>
                                            <GlassDatePicker
                                                id={`${fieldIdPrefix}-${name}`}
                                                name={name}
                                                withTime
                                                portal
                                                ariaLabel={fieldLabel}
                                                value={values[name]}
                                                onChange={onFieldChange(name)}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    );
                                })}
                            </>
                        )}
                        <PanelInputField
                            as="textarea"
                            className="discover-composer-card__input-field discover-composer-card__input-field--full"
                            label={t('discoverPage.composer.descriptionLabel', 'Description')}
                            id={`${fieldIdPrefix}-description`}
                            name="description"
                            value={values.description}
                            onChange={onFieldChange('description')}
                            disabled={isSubmitting}
                            rows={3}
                        />
                    </div>
                    <p className="discover-composer-card__tip">
                        {t('discoverPage.composer.mapHint', 'Tip: click on the map to copy coordinates and resolve the address.')}
                    </p>
                    {errorMessage && (
                        <p className="discover-composer-card__error" role="alert">{errorMessage}</p>
                    )}
                    <div className="discover-composer-card__actions">
                        <Button
                            type="button"
                            variant="secondary"
                            size="small"
                            shape="pill"
                            className="discover-composer-card__button discover-composer-card__button--cancel"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            {cancelLabel}
                        </Button>
                        <Button
                            type="submit"
                            variant="gradient"
                            size="small"
                            shape="pill"
                            className="discover-composer-card__button discover-composer-card__button--save"
                            disabled={isSaveDisabled}
                        >
                            {isSubmitting ? savingLabel : saveLabel}
                        </Button>
                    </div>
                </form>
            )}
        </section>
    );
}

DiscoverMarkerComposerCard.propTypes = {
    t: PropTypes.func.isRequired,
    children: PropTypes.node,
    isEditing: PropTypes.bool,
    isTypeLocked: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    isLocationResolving: PropTypes.bool,
    isSaveDisabled: PropTypes.bool,
    values: PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
        latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        city: PropTypes.string,
        stateOrRegion: PropTypes.string,
        country: PropTypes.string,
        countryCode: PropTypes.string,
        address: PropTypes.string,
        description: PropTypes.string,
        startDate: PropTypes.string,
        endDate: PropTypes.string,
        privacy: PropTypes.string,
    }).isRequired,
    markerTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
    errorMessage: PropTypes.string,
    addressValue: PropTypes.string,
    addressFieldRef: PropTypes.shape({ current: PropTypes.any }),
    addressSuggestions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string.isRequired,
    })),
    addressHighlightedIndex: PropTypes.number,
    isAddressOpen: PropTypes.bool,
    isAddressLoading: PropTypes.bool,
    showAddressNoResults: PropTypes.bool,
    addressLookupError: PropTypes.shape({
        message: PropTypes.string,
    }),
    locationError: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
    onAddressChange: PropTypes.func.isRequired,
    onAddressKeyDown: PropTypes.func.isRequired,
    onAddressFieldBlur: PropTypes.func.isRequired,
    onOpenAddressDropdown: PropTypes.func.isRequired,
    onAddressSuggestionSelect: PropTypes.func.isRequired,
    onAddressSuggestionHighlight: PropTypes.func.isRequired,
};
